// The three states before a recruiter sees the brain — TODO T17 (30 Aug 2026), redrawn to v2
// at T30 (1 Sep 2026).
//
// Signed out, signed in but not linked to a staff row, and a brain that could not start. The
// middle one is the reason this file exists: since T17 the BFF refuses a token whose subject has
// no ACTIVE `staff_operators` row (services/bff/src/auth.ts), which is the state EVERY newly
// invited recruiter is in until an operator links them. Rendering that as "signed out" would loop
// them through Clerk forever; rendering it as an error would tell them nothing they can act on.
// So it gets a screen that names the fix and prints the statement with their Clerk user id
// already in it — the brain cannot run that statement itself, and must not be able to (the BFF
// holds SELECT on that table and nothing more).
import { SignIn, SignOutButton, useUser } from '@clerk/react';
import type { ReactNode } from 'react';
import darkLogo from '../assets/kadmon-logo-dark.png';
import lightLogo from '../assets/kadmon-logo-light.png';
import { Badge, Button } from '../ui/atoms.tsx';
import { VoiceOrb } from './VoiceOrb.tsx';
import { useTheme } from './theme.tsx';
import '../styles/screens/gate.css';

/**
 * The frame every gate shares: the card on the page ground (T392a took the funnel's radar out;
 * styles/screens/gate.css).
 */
function Frame({ children }: { children: ReactNode }) {
  return (
    <div className="gate">
      <div className="gate__card">
        <div className="gate__brand">
          <VoiceOrb state="listening" size={38} className="brand-orb" />
          <span>
            <img className="sidebar__logo sidebar__logo--on-dark" src={lightLogo} alt="kadmon" />
            <img className="sidebar__logo sidebar__logo--on-light" src={darkLogo} alt="kadmon" />
            <span className="sidebar__eyebrow">Brain</span>
          </span>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * The value a token resolves to right now, as a literal colour.
 *
 * Clerk's `appearance.variables` are not passed through to CSS untouched — it derives hover and
 * disabled shades from them, which means it has to PARSE them. A `var(--accent)` would not parse
 * and the card would silently fall back to Clerk's own palette on kadmon's front door. So the
 * theme's current value is read off the document and handed over as a literal, which also means
 * the card follows the theme and accent: `useTheme` re-renders this when the recruiter switches.
 */
function token(name: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value === '' ? fallback : value;
}

export function SignInGate() {
  // Read, not ignored: a recruiter who chose dark mode or another accent and signed out sees that
  // sign-in card.
  const { theme, accent } = useTheme();

  return (
    <Frame>
      {/* Clerk's own component, per the T16 decision: a hosted sign-in is the whole reason this
          is Clerk rather than a hand-rolled OAuth+PKCE flow (services/bff/README.md § Auth). */}
      <SignIn
        key={`${theme}-${accent}`}
        appearance={{
          variables: {
            colorBackground: token('--bg-1', '#FFFFFF'),
            colorForeground: token('--ink-1', '#0F0F0E'),
            colorPrimary: token('--accent', '#1E3D33'),
            colorInput: token('--bg-2', '#FFFFFF'),
            borderRadius: '6px',
            fontFamily: "'Geist', 'Helvetica Neue', system-ui, sans-serif",
          },
          elements: {
            // The instance is Invite-only, so "Don't have an account? Sign up" leads to a
            // refusal. A dead end on the front door of an internal tool is worse than no link.
            footerAction: { display: 'none' },
          },
        }}
      />
      <p className="micro">Invite only · staff accounts</p>
    </Frame>
  );
}

export function NotARecruiterGate() {
  const { user } = useUser();
  const clerkUserId = user?.id ?? 'user_…';
  const email = user?.primaryEmailAddress?.emailAddress ?? null;

  return (
    <Frame>
      <Badge tone="warn">Signed in · not linked</Badge>
      <h1 className="gate__title">This account is not linked to a kadmon staff record</h1>
      <p className="gate__body">
        You are signed in{email ? ` as ${email}` : ''} and Clerk accepted the session; what is
        missing is the other half of the check: an active <code>staff_operators</code> row carrying
        this Clerk user id. The brain cannot create one, deliberately.
      </p>
      <p className="gate__body">An operator links it with one statement against the database:</p>
      <code className="gate__code">
        {`update staff_operators\n   set clerk_user_id = '${clerkUserId}'\n where e164 = '<your staff number>';`}
      </code>
      <p className="gate__body dim">
        It runs as <code>DATABASE_URL</code>, never as the brain. See <code>packages/db</code>
        &rsquo;s README, § Roles and connection URLs. Then reload this page.
      </p>
      <div className="button-row">
        <Button onClick={() => window.location.reload()}>Reload</Button>
        <SignOutButton>
          <Button kind="quiet" icon="logout">
            Sign out
          </Button>
        </SignOutButton>
      </div>
    </Frame>
  );
}

export function StartupFailed({ error }: { error: Error }) {
  return (
    <Frame>
      <h1 className="gate__title">The brain did not start</h1>
      <code className="gate__code">{error.message}</code>
    </Frame>
  );
}
